import { createRef, type FormEvent, type KeyboardEvent } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import {
  FlyToInterpolator,
  Widget,
  type MapViewState,
  type Viewport,
  type WidgetPlacement,
  type WidgetProps,
} from '@deck.gl/core';
import { SendOutlined } from '@ant-design/icons';

type LocatorWidgetProps = WidgetProps & {
  placement?: WidgetPlacement;
  viewId?: string | null;
  transitionDuration?: number;
  labels?: {
    longitude: string;
    latitude: string;
    zoom: string;
    move: string;
  };
};

const LONGITUDE_PATTERN =
  /^-?(180(?:\.0+)?|1[0-7][0-9](?:\.[0-9]+)?|[1-9]?[0-9](?:\.[0-9]+)?)$/;
const LATITUDE_PATTERN = /^-?(90(?:\.0+)?|[0-8]?[0-9](?:\.[0-9]+)?)$/;
const ZOOM_PATTERN = /^(20(\.0+)?|1?[0-9](?:\.[0-9]+)?)$/;

class LocatorWidget extends Widget<LocatorWidgetProps> {
  static defaultProps: Required<LocatorWidgetProps> = {
    ...Widget.defaultProps,
    id: 'locator',
    placement: 'top-left',
    viewId: null,
    transitionDuration: 200,
    labels: {
      longitude: 'Lon',
      latitude: 'Lat',
      zoom: 'Zoom',
      move: 'Move to the point',
    },
  };

  root: Root | null = null;
  className = 'deck-widget-locator';
  placement: WidgetPlacement = 'top-left';
  viewStates: Record<string, MapViewState> = {};
  longitude: string = '';
  latitude: string = '';
  zoom: string = '';
  isLongitudeError: boolean = false;
  isLatitudeError: boolean = false;
  isZoomError: boolean = false;
  longitudeInputRef = createRef<HTMLInputElement>();
  latitudeInputRef = createRef<HTMLInputElement>();
  zoomInputRef = createRef<HTMLInputElement>();

  constructor(props: LocatorWidgetProps) {
    super(props);
    this.setProps(this.props);
  }

  public setProps(props: Partial<LocatorWidgetProps>): void {
    super.setProps(props);

    const viewState = Object.values(this.viewStates)[0] ?? null;

    this.placement = props.placement ?? this.placement;
    this.viewId = props.viewId ?? this.viewId;
    this.longitude = viewState ? viewState.longitude.toFixed(6) : '';
    this.latitude = viewState ? viewState.latitude.toFixed(6) : '';
    this.zoom = viewState ? viewState.zoom.toFixed(2) : '';
  }

  public onAdd(): HTMLDivElement {
    const rootElement = this.onCreateRootElement();
    this.root = createRoot(rootElement);
    return rootElement;
  }

  public onRemove(): void {
    this.root?.unmount();
    this.root = null;
  }

  public onRenderHTML(): void {
    this.root?.render(
      <div className="deck-widget-locator">
        <LocatorContainer>
          <InputContainer>
            <StyledLabel>{this.props.labels.longitude}</StyledLabel>
            <StyledInput
              ref={this.longitudeInputRef}
              type="text"
              name="longitude"
              value={this.longitude}
              width={82}
              data-is-error={this.isLongitudeError}
              onChange={(event: FormEvent<HTMLInputElement>): void => {
                this.longitude = event.currentTarget.value
                  .trim()
                  .replaceAll(/[^\d.-]/g, '')
                  .substring(0, 11);
                this.updateHTML();
              }}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>): void => {
                if (event.key === 'Enter') {
                  this.handleMapMove();
                }
                // 末尾でRightキーを押すと、緯度入力欄にフォーカスが移動するようにする
                if (
                  ['Right', 'ArrowRight'].includes(event.key) &&
                  event.currentTarget.selectionStart ===
                    event.currentTarget.value.length
                ) {
                  event.preventDefault();
                  this.latitudeInputRef.current?.focus();
                }
              }}
            />
            °
          </InputContainer>
          <InputContainer>
            <StyledLabel>{this.props.labels.latitude}</StyledLabel>
            <StyledInput
              ref={this.latitudeInputRef}
              type="text"
              name="latitude"
              value={this.latitude}
              width={82}
              data-is-error={this.isLatitudeError}
              onChange={(event: FormEvent<HTMLInputElement>): void => {
                this.latitude = event.currentTarget.value
                  .trim()
                  .replaceAll(/[^\d.-]/g, '')
                  .substring(0, 11);
                this.updateHTML();
              }}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>): void => {
                if (event.key === 'Enter') {
                  this.handleMapMove();
                }
                // 先頭でLeftキーを押すと、経度入力欄にフォーカスが移動するようにする
                if (
                  ['Left', 'ArrowLeft'].includes(event.key) &&
                  event.currentTarget.selectionEnd === 0
                ) {
                  event.preventDefault();
                  this.longitudeInputRef.current?.focus();
                }
                // 末尾でRightキーを押すと、ズームレベル入力欄にフォーカスが移動するようにする
                if (
                  ['Right', 'ArrowRight'].includes(event.key) &&
                  event.currentTarget.selectionStart ===
                    event.currentTarget.value.length
                ) {
                  event.preventDefault();
                  this.zoomInputRef.current?.focus();
                }
              }}
            />
            °
          </InputContainer>
          <InputContainer>
            <StyledLabel>{this.props.labels.zoom}</StyledLabel>
            <StyledInput
              ref={this.zoomInputRef}
              type="text"
              name="zoom"
              value={this.zoom}
              width={44}
              data-is-error={this.isZoomError}
              onChange={(event: FormEvent<HTMLInputElement>): void => {
                this.zoom = event.currentTarget.value
                  .trim()
                  .replaceAll(/[^\d.]/g, '')
                  .substring(0, 5);
                this.updateHTML();
              }}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>): void => {
                if (event.key === 'Enter') {
                  this.handleMapMove();
                }
                // 先頭でLeftキーを押すと、緯度入力欄にフォーカスが移動するようにする
                if (
                  ['Left', 'ArrowLeft'].includes(event.key) &&
                  event.currentTarget.selectionEnd === 0
                ) {
                  event.preventDefault();
                  this.latitudeInputRef.current?.focus();
                }
              }}
            />
          </InputContainer>
          <Tooltip placement="top" title={this.props.labels.move}>
            <MoveButton
              type="button"
              onClick={(): void => {
                this.handleMapMove();
              }}
            >
              <SendOutlined />
            </MoveButton>
          </Tooltip>
        </LocatorContainer>
      </div>
    );
  }

  public onViewportChange(viewport: Viewport): void {
    const currentViewState = this.viewStates?.[viewport.id] ?? null;
    const nextViewState = this.getViewState(viewport.id);

    // 現在のビュー状態と新しいビュー状態が大きく変化した場合のみ更新する
    if (
      !currentViewState ||
      currentViewState.longitude.toFixed(3) !==
        nextViewState.longitude.toFixed(3) ||
      currentViewState.latitude.toFixed(3) !==
        nextViewState.latitude.toFixed(3) ||
      currentViewState.zoom.toFixed(2) !== nextViewState.zoom.toFixed(2)
    ) {
      this.viewStates = {
        ...this.viewStates,
        [viewport.id]: nextViewState,
      };
      this.isLongitudeError = false;
      this.isLatitudeError = false;
      this.isZoomError = false;
      this.longitude = nextViewState.longitude.toFixed(6);
      this.latitude = nextViewState.latitude.toFixed(6);
      this.zoom = nextViewState.zoom.toFixed(2);
      this.updateHTML();
    }
  }

  protected getViewState(viewId: string): MapViewState {
    const viewState = super.getViewState(viewId);

    if (
      typeof viewState.latitude === 'number' &&
      typeof viewState.longitude === 'number' &&
      typeof viewState.zoom === 'number'
    ) {
      return {
        ...viewState,
        latitude: viewState.latitude,
        longitude: viewState.longitude,
        zoom: viewState.zoom,
      };
    }
    return {
      ...viewState,
      longitude: Number.NaN,
      latitude: Number.NaN,
      zoom: Number.NaN,
    };
  }

  private handleMapMove(): void {
    if (!LONGITUDE_PATTERN.test(this.longitude)) {
      this.isLongitudeError = true;
    }
    if (!LATITUDE_PATTERN.test(this.latitude)) {
      this.isLatitudeError = true;
    }
    if (!ZOOM_PATTERN.test(this.zoom)) {
      this.isZoomError = true;
    }
    if (
      LONGITUDE_PATTERN.test(this.longitude) &&
      LATITUDE_PATTERN.test(this.latitude) &&
      ZOOM_PATTERN.test(this.zoom)
    ) {
      this.isLatitudeError = false;
      this.isLongitudeError = false;
      this.isZoomError = false;

      // 全てのビューに対して、地図を指定された緯度・経度・ズームレベルに移動させる
      for (const viewId of this.viewIds) {
        const currentViewState = this.getViewState(viewId);
        const nextViewState = {
          ...currentViewState,
          longitude: Number(this.longitude),
          latitude: Number(this.latitude),
          zoom: Number(this.zoom),
        };
        this.setViewState(viewId, {
          ...nextViewState,
          transitionDuration: this.props.transitionDuration,
          transitionInterpolator: new FlyToInterpolator(),
        });
      }
    }
    // エラー表示のオン・オフのためにHTMLを更新する
    this.updateHTML();
  }
}

const LocatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 28px;
  box-sizing: border-box;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.25);
  cursor: default;
  border-radius: 8px;
  padding: 1px;
  gap: 1px;
  pointer-events: auto;
  & > * {
    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }
    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  background-color: #ffffff;
  padding: 2px 8px;
`;

const StyledLabel = styled.span`
  font-size: 10px;
  color: #000000;
  margin-right: 2px;
`;

const StyledInput = styled.input<{ width?: number; 'data-is-error'?: boolean }>`
  width: ${(props) => (props.width ? `${props.width}px` : 'fit-content')};
  padding: 0 4px;
  pointer-events: auto;
  border: ${(props) =>
    props['data-is-error'] ? '1px solid #ED1A3F' : '1px solid #0000004D'};
  border-style: ${(props) =>
    props['data-is-error'] ? 'solid' : 'none none solid none'};
  border-radius: 2px;
  box-sizing: border-box;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  &:focus {
    outline: none;
  }
`;

const MoveButton = styled.button`
  width: 28px;
  background-color: #ffffff;
  border: none;
  padding: 2px 4px;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

export { LocatorWidget };
